/** Blender web KYC gate — same implementation for desktop and mobile renderers.
 * Usage: if (!await requireKYC({theme:'light',language:'english'})) return;
 *        openExistingBlenderPasswordPrompt();
 * Never pass usernames or Stripe secret keys from the client.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';

export const KYC_LAMBDA_URL = 'https://zzsn2te5fwzcosparqszqtd6ge0aeggw.lambda-url.us-east-1.on.aws/';
const GREEN = '#009e81';
const API = KYC_LAMBDA_URL.replace(/\/$/, '');
let activePrompt = null;

async function call(action, method='GET') {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken();
  const user = await Auth.currentAuthenticatedUser();
  const response = await fetch(`${API}/kyc/${action}`, {
    method, headers: { Authorization:`Bearer ${token}`, 'Content-Type':'application/json' }, cache:'no-store'
  });
  if (!response.ok) throw new Error(`Unable to check identity verification (${response.status}).`);
  const data = await response.json();
  if (data.userId !== user?.attributes?.sub) throw new Error('Signed-in account does not match KYC record.');
  return data;
}
export const getKYCStatus = () => call('status');
export const startKYC = () => call('start','POST');
export const refreshKYC = () => call('refresh','POST');
export const isKYCApproved = d => d?.verified === true && d?.kycStatus === 'approved' && d?.financialAccess === 'enabled';

// The web app stores its active light/dark setting under mode_light_dark.
// Automatic page-level gates may not receive a theme prop, so read the current
// application setting when the prompt opens instead of defaulting to white.
function resolveKYCTheme(theme) {
  try {
    const saved = window.localStorage.getItem('mode_light_dark');
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (_) { /* Storage can be unavailable in restricted browser contexts. */ }
  const explicit = String(theme ?? '').toLowerCase();
  if (explicit === 'light' || explicit === 'dark') return explicit;
  const page = document.querySelector('.tbp-platform--dark, .tbp-platform--light');
  if (page?.classList.contains('tbp-platform--dark')) return 'dark';
  if (page?.classList.contains('tbp-platform--light')) return 'light';
  return 'light';
}

export default async function requireKYC({theme, language='english'}={}) {
  try {
    const initial = await getKYCStatus();
    if (isKYCApproved(initial)) return true;
  } catch (e) {
    // Fail closed, but display an actionable error rather than continuing to password.
    return showGate({ theme, language, initialError:e.message });
  }
  return showGate({theme,language});
}

function showGate({theme,language,initialError=''}) {
  if (activePrompt) return activePrompt;
  activePrompt = new Promise(resolve => {
    const normalizedTheme = resolveKYCTheme(theme);
    const normalizedLanguage = String(language ?? 'english').toLowerCase();
    const dark = normalizedTheme === 'dark';
    const spanish = normalizedLanguage.startsWith('es') || normalizedLanguage === 'spanish';
    const t = (en,es) => spanish?es:en;
    const backdrop=document.createElement('div');
    backdrop.setAttribute('role','presentation');
    backdrop.style.cssText='position:fixed;inset:0;z-index:2147483640;display:flex;align-items:center;justify-content:center;padding:18px;background:rgba(0,0,0,.65)';
    const dialog=document.createElement('section');
    dialog.setAttribute('role','dialog');dialog.setAttribute('aria-modal','true');dialog.setAttribute('aria-label',t('Identity verification','Verificación de identidad'));
    dialog.style.cssText=`box-sizing:border-box;width:100%;max-width:440px;padding:24px;border-radius:20px;border:1px solid ${dark?'#30554c':'#d7e4e2'};background:${dark?'#172727':'#fff'};color:${dark?'#f4fbf9':'#203447'};box-shadow:0 22px 80px rgba(0,0,0,.22);font-family:inherit;`;
    dialog.style.setProperty('text-align','left','important');
    const title=document.createElement('h2');title.textContent=t('Verify Your Identity','Verifica Tu Identidad');title.style.cssText='font-size:23px;margin:0 0 12px;';
    const desc=document.createElement('p');desc.textContent=t('Verification is required before this transaction. Your Blender password will be requested only after approval.','Debes verificar tu identidad antes de esta transacción. Se solicitará tu contraseña de Blender solo después de la aprobación.');desc.style.cssText='font-size:15px;line-height:1.55;margin:0 0 18px;';
    const msg=document.createElement('p');msg.setAttribute('role','status');msg.style.cssText=`font-size:14px;line-height:1.45;min-height:18px;margin:0 0 14px;color:${dark?'#a3e5d8':'#40645e'};`;
    // Override app-wide paragraph/heading centering without changing button alignment.
    [title,desc,msg].forEach(el => el.style.setProperty('text-align','left','important'));
    msg.textContent=initialError || '';
    const button=(label,primary,onClick)=>{const b=document.createElement('button');b.type='button';b.textContent=label;b.style.cssText=`display:block;width:100%;border:1px solid ${GREEN};border-radius:12px;cursor:pointer;padding:13px 16px;margin:10px 0 0;font-weight:700;font-size:15px;background:${primary?GREEN:'transparent'};color:${primary?'white':dark?'#f4fbf9':'#203447'};`;b.style.setProperty('text-align','center','important');b.onclick=onClick;return b;};
    let finished=false, busy=false, started=false;
    const cleanup = value => {if(finished)return;finished=true;window.removeEventListener('focus',check);document.removeEventListener('visibilitychange',visible);clearInterval(interval);backdrop.remove();activePrompt=null;resolve(value);};
    const check=async()=>{
      if(finished || busy)return;busy=true;
      try{const r=await refreshKYC();if(isKYCApproved(r))return cleanup(true);msg.textContent=r.stripeStatus==='processing'?t('Stripe is processing your verification.','Stripe está procesando tu verificación.'):t('Verification not yet approved.','La verificación aún no ha sido aprobada.');}
      catch(e){msg.textContent=e.message||t('Unable to check verification.','No se pudo comprobar la verificación.');}
      finally{busy=false;}
    };
    const visible=()=>{if(!document.hidden)check();};
    const verifyBtn=button(t('Verify with Stripe','Verificar con Stripe'),true,async()=>{
      if(busy || finished)return;
      // Synchronous window opening avoids popup blockers.
      const tab=window.open('about:blank','_blank');
      busy=true;verifyBtn.disabled=true;
      try{
        const result=await startKYC();
        if(isKYCApproved(result)){if(tab)tab.close();return cleanup(true);}
        if(result.url && /^https:\/\/verify\.stripe\.com\//.test(result.url)){
          started=true;if(tab)tab.location.replace(result.url);else window.location.assign(result.url);
          msg.textContent=t('Complete verification in the new tab, then return here.','Completa la verificación en la nueva pestaña y vuelve aquí.');
        } else {if(tab)tab.close();msg.textContent=t('Verification is processing. Check again shortly.','La verificación está en proceso. Comprueba de nuevo en breve.');}
      }catch(e){if(tab)tab.close();msg.textContent=e.message||t('Unable to start verification.','No se pudo iniciar la verificación.');}
      finally{busy=false;verifyBtn.disabled=false;}
    });
    const checkBtn=button(t('I completed verification — check status','Ya completé la verificación — comprobar estado'),false,check);
    const cancelBtn=button(t('Not now','Ahora no'),false,()=>cleanup(false));
    dialog.append(title,desc,msg,verifyBtn,checkBtn,cancelBtn);backdrop.append(dialog);document.body.append(backdrop);
    window.addEventListener('focus',check);document.addEventListener('visibilitychange',visible);
    const interval=setInterval(()=>{if(started&&!document.hidden)check();},8000);
    cancelBtn.focus();
  });
  return activePrompt;
}

// Backward-compatible wrapper used by the shared PasswordPrompt/CryptoMoneyPanel
// in the previously delivered mobile + desktop web renderers.
export function KYCProtected({children,enabled=true,onCancel,theme,language='english'}) {
  const [allowed,setAllowed]=useState(false);
  const resolvedTheme = resolveKYCTheme(theme);
  const [retry,setRetry]=useState(0);
  const [checked,setChecked]=useState(false);
  const generation=useRef(0);
  useEffect(()=>{
    const current=++generation.current;
    if(!enabled){setAllowed(false);return;}
    let alive=true;
    requireKYC({theme,language}).then(ok=>{
      if(!alive||generation.current!==current)return;
      setAllowed(ok);
      setChecked(true);
      if(!ok && onCancel) onCancel();
    });
    return()=>{alive=false;};
  },[enabled,theme,language,retry]);
  // A canceled deposit check should not expose addresses or leave an empty panel.
  // Allow the member to restart verification without refreshing the entire app.
  useEffect(()=>{setChecked(false);},[enabled,retry]);
  return enabled && allowed ? <>{children}</> : enabled && checked && !onCancel ?
    <div ref={node => { if (node) node.style.setProperty('text-align', 'left', 'important'); }} style={{padding:24,textAlign:'left',background:resolvedTheme==='dark'?'#11283a':'#fff',color:resolvedTheme==='dark'?'#f4fbf9':'#253c38',borderRadius:0}}>
      <p ref={node => { if (node) node.style.setProperty('text-align', 'left', 'important'); }} style={{textAlign:'left',marginLeft:0,marginRight:0}}>{String(language ?? 'english').toLowerCase().startsWith('es')||String(language ?? '').toLowerCase()==='spanish'?'Se requiere verificación de identidad antes de mostrar las direcciones de depósito.':'Identity verification is required before deposit addresses can be displayed.'}</p>
      <button type="button" style={{background:'#009e81',color:'#fff',border:0,borderRadius:10,padding:'12px 20px',cursor:'pointer'}} onClick={()=>setRetry(n=>n+1)}>
        {String(language ?? 'english').toLowerCase().startsWith('es')||String(language ?? '').toLowerCase()==='spanish'?'Verificar identidad':'Verify identity'}
      </button>
    </div> : null;
}
